// Jumbo reactions (#714): the rules of the hold. Press and hold an emoji; the app shows no timer, bar or hint.
//
//   under 0.45 s   a tap: a normal pick
//   0.45 to 1.5 s  let go: a super reaction (#87), whatever the Super switch says
//   1.5 to 4 s     the emoji grows smoothly from 100% to 300%; let go: a jumbo reaction
//   4 to 6 s       it stays at 300% and trembles, more and more; let go: a jumbo reaction
//   6 s            it turns into a splat (with a sound); nothing is sent, and the next hold starts over
//
// A pointer that leaves the emoji, or a touch that is cancelled, sends nothing. A finger that has held 0.45 s may drift:
// it covers the emoji, so the person moves it to look. It has left the emoji only when it is HOLD_SLOP_PX outside it.
//
// This file is the one source of the numbers and of the state machine. It is pure: it reads no clock, so a test (or a
// port in Swift or Kotlin) feeds it the time. `shared/cases/jumbo-hold.json` holds the cases every client runs.

/** All the thresholds, in one block. Times are ms since the press. */
export const JUMBO_HOLD = {
  /** Let go from here: a super reaction (#87). Before it: a tap. */
  superMs: 450,
  /** From here the emoji grows, and a release is jumbo. */
  growStartMs: 1500,
  /** Full size from here; the tremble starts. */
  growEndMs: 4000,
  /** The splat: nothing is sent. */
  splatMs: 6000,
  /** Full size, as a multiple of the normal size (300%). */
  maxScale: 3,
  /** The tremble's reach in points or px (a fraction of this at first, all of it at the splat), on the x and y axes. */
  trembleReach: 2.5,
  /** The tremble's speed on each axis, in radians per second (different, so it wanders and doesn't draw a line). */
  trembleRadX: 61,
  trembleRadY: 47,
} as const;

/** How far outside the emoji a held finger may drift (px or points) before it has left it (#726). */
export const HOLD_SLOP_PX = 30;

/**
 * Whether a pointer at `x, y` is still on the emoji whose box is `rect` (`[left, top, width, height]`). A mouse, and a finger
 * in the first 0.45 s, must be inside the box; a finger that has held for `heldMs` >= 0.45 s may be HOLD_SLOP_PX outside it.
 */
export function holdStillOn(x: number, y: number, rect: readonly [number, number, number, number], touch: boolean, heldMs: number): boolean {
  const slop = touch && heldMs >= JUMBO_HOLD.superMs ? HOLD_SLOP_PX : 0;
  const [left, top, width, height] = rect;
  return x >= left - slop && x <= left + width + slop && y >= top - slop && y <= top + height + slop;
}

/** What a jumbo reaction is marked with in "Who reacted": "bob, charlie, evan 3x". */
export const JUMBO_MARK = `${JUMBO_HOLD.maxScale}x`;

/** A jumbo counter draws its emoji at this multiple of the normal size. */
export const JUMBO_COUNTER_SCALE = 2;

export type HoldStage = "tap" | "super" | "grow" | "full" | "splat";

/** The stage at `ms` since the press. Each stage starts exactly at its threshold. */
export function holdStage(ms: number): HoldStage {
  if (ms >= JUMBO_HOLD.splatMs) return "splat";
  if (ms >= JUMBO_HOLD.growEndMs) return "full";
  if (ms >= JUMBO_HOLD.growStartMs) return "grow";
  if (ms >= JUMBO_HOLD.superMs) return "super";
  return "tap";
}

/** The size at `ms`, as a multiple of the normal size: 1 until the grow starts, straight up to `maxScale`, then flat. */
export function holdScale(ms: number): number {
  const { growStartMs: a, growEndMs: b, maxScale } = JUMBO_HOLD;
  if (ms <= a) return 1;
  if (ms >= b) return maxScale;
  return 1 + ((maxScale - 1) * (ms - a)) / (b - a);
}

/** The tremble's offset at `ms`: none until full size, then growing evenly to `trembleReach` at the splat. */
export function holdTremble(ms: number): { x: number; y: number } {
  const { growEndMs: b, splatMs: c, trembleReach, trembleRadX, trembleRadY } = JUMBO_HOLD;
  if (ms <= b) return { x: 0, y: 0 };
  const amp = trembleReach * Math.min(1, (ms - b) / (c - b));
  const s = ms / 1000;
  return { x: Math.sin(s * trembleRadX) * amp, y: Math.cos(s * trembleRadY) * amp };
}

/** What a release sends. `normal` is a tap: the client's own click does it. */
export type HoldSend = "normal" | "super" | "jumbo";

export type HoldState = { phase: "idle" } | { phase: "held"; since: number; splatted: boolean };

export type HoldEvent =
  | { t: "press"; at: number }
  /** The clock moved on (a frame): lets the machine notice the splat. */
  | { t: "tick"; at: number }
  | { t: "release"; at: number }
  /** The pointer left the emoji. */
  | { t: "leave"; at: number }
  /** The touch was cancelled (a scroll took it, a call came in). */
  | { t: "cancel"; at: number };

export interface HoldResult {
  state: HoldState;
  /** The stage now; null when nothing is held. */
  stage: HoldStage | null;
  /** Set once per hold, by the event that reaches 6 s: play the splat sound, show the splat. */
  splat?: true;
  /** Set by a release that sends something. A release after the splat sends nothing. */
  send?: HoldSend;
}

export const IDLE: HoldState = { phase: "idle" };

/** One step of the machine. Pure: the same state and event always give the same result. */
export function holdStep(state: HoldState, e: HoldEvent): HoldResult {
  if (e.t === "press") {
    // A second press while one is held (a second finger) changes nothing.
    if (state.phase === "held") return { state, stage: holdStage(e.at - state.since) };
    return { state: { phase: "held", since: e.at, splatted: false }, stage: "tap" };
  }
  if (state.phase === "idle") return { state, stage: null };
  const ms = e.at - state.since;
  const stage = holdStage(ms);
  const splatNow = stage === "splat" && !state.splatted;
  if (e.t === "tick") {
    const next: HoldState = splatNow ? { ...state, splatted: true } : state;
    return { state: next, stage, ...(splatNow ? { splat: true as const } : {}) };
  }
  if (e.t === "release") {
    // At or after the splat nothing is sent. The hold that splatted at the very release still gets its splat.
    if (stage === "splat") return { state: IDLE, stage, ...(splatNow ? { splat: true as const } : {}) };
    return { state: IDLE, stage, send: stage === "tap" ? "normal" : stage === "super" ? "super" : "jumbo" };
  }
  // leave, cancel: nothing is sent, whatever the stage.
  return { state: IDLE, stage };
}
